import cn from 'classnames';
import { isEqual } from 'lodash';
import { useRouter } from 'next/router';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Trans, useTranslation } from 'react-i18next';
import { ButtonV3 } from 'components/ComponentV2';
import { confirmationModalInstance } from 'components/ComponentV2/ConfirmationModal';
import CustomQuestionModal from 'components/CustomQuestionModal';
import Header from 'components/Header';
import Tooltip from 'components/Tooltip';
import TouchpointQuestions from 'components/TouchpoinQuestions';
import { Checkbox, Input, Toggle } from 'components/form';
import { Row } from 'components/layout';

import { ConfirmationButtonResponse } from 'hooks/UseCompanyCampaigns';
import { useEvent } from 'hooks/useEvent';

import { QuestionState } from 'lib/models/answers';
import { CompanyEventRequestStep3, EventAttributes } from 'lib/models/event';
import { TouchpointState } from 'lib/models/opportunity';
import { TouchpointType } from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { REGEX } from 'lib/utils/regex';

import { useCampaigns } from 'lib/contexts/campaigns-context';
import { getOpportunityPublicPagePathname } from 'utils/urls';
import styles from '../Step.module.scss';

export interface Step3Props {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  step: number;
  touchpoint: EventAttributes | null;
  onSave: ({
    data,
    isValueEdited,
    goToStep,
    isPreviewClicked,
  }: {
    data: CompanyEventRequestStep3;
    isValueEdited: boolean;
    goToStep?: number;
    isPreviewClicked?: boolean;
  }) => void;
  isDraftLoading: boolean;
  isLoading: boolean;
  isSocietyEvent?: boolean;
}

const QuestionOptions = [
  {
    id: '1',
    label: QuestionState.OFF,
    value: QuestionState.OFF,
  },
  {
    id: '2',
    label: QuestionState.OPTIONAL,
    value: QuestionState.OPTIONAL,
  },
  {
    id: '3',
    label: QuestionState.REQUIRED,
    value: QuestionState.REQUIRED,
    groupEnd: true,
  },
  {
    id: '4',
    label: QuestionState.EDIT,
    value: QuestionState.EDIT,
    isEdit: true,
  },
  {
    id: '5',
    label: QuestionState.DELETE,
    value: QuestionState.DELETE,
    isDelete: true,
  },
];

export const Step3: React.FC<Step3Props> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    step,
    touchpoint,
    onSave,
    isDraftLoading,
    isLoading,
    isSocietyEvent,
  }: Step3Props) => {
    const contentClassNames = cn(
      {
        [styles.Step]: true,
      },
      className
    );
    const router = useRouter();
    const { questionTypes } = useEvent();
    const { onboarding } = router.query;
    const [t] = useTranslation('opportunity.posting');

    const { isManager } = useAuth();

    const { mutateEvents } = useCampaigns();

    const [touchpointableId, setTouchpointableId] = useState<string | null>(
      null
    );
    const [state, setState] = useState<string>();
    const [enforceCapacity, setEnforceCapacity] = useState(false);
    const [capacity, setCapacity] = useState<number>();
    const [enableWaitlist, setEnableWaitlist] = useState(false);
    const [showGuestList, setShowGuestList] = useState(false);
    const [enableForceWaitlist, setEnableForceWaitlist] = useState(false);

    const [isOpenCustomQuestionModal, setIsOpenCustomQuestionModal] =
      useState(false);
    const [customQuestionsList, setCustomQuestionsList] = useState<
      Array<{
        id?: string;
        title: string;
        state: QuestionState;
        isNew?: boolean;
        options?: Array<string>;
        question_type: string;
      }>
    >([]);
    const [removedCustomQuestionIds, setRemovedCustomQuestionIds] = useState<
      Array<string>
    >([]);
    const [isUniversityRequiredState, setUniversityRequiredState] =
      useState<QuestionState>(QuestionState.OFF);
    const [isStudyYearRequiredState, setStudyYearRequiredState] =
      useState<QuestionState>(QuestionState.OFF);
    const [isDegreeRequiredState, setDegreeRequiredState] =
      useState<QuestionState>(QuestionState.OFF);
    const [isSubjectRequiredState, setSubjectRequiredState] =
      useState<QuestionState>(QuestionState.OFF);
    const [isCVRequiredState, setCVRequiredState] = useState<QuestionState>(
      QuestionState.OFF
    );
    const [isLinkedInProfileRequiredState, setLinkedInProfileRequiredState] =
      useState<QuestionState>(QuestionState.OFF);
    const [availableTouchpointData, setAvailableTouchpointData] =
      useState<CompanyEventRequestStep3>();
    const [selectedQuestionId, setSelectedQuestionId] = useState('');
    const [selectedQuestion, setSelectedQuestion] = useState<{
      id?: string;
      title?: string;
      state?: QuestionState;
      isNew?: boolean;
      options?: Array<string>;
      question_type: string;
    } | null>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isNotifySubscribers, setIsNotifySubscribers] = useState(true);

    useEffect(() => {
      if (!isOpenCustomQuestionModal) {
        setSelectedQuestionId('');
        setSelectedQuestion(null);
      }
    }, [isOpenCustomQuestionModal]);

    useEffect(() => {
      if (selectedQuestionId) {
        const question = customQuestionsList.find(
          (ques) => ques.id == selectedQuestionId
        );
        setSelectedQuestion(question || null);
        setIsOpenCustomQuestionModal(true);
      }
    }, [selectedQuestionId]);

    const questions = useMemo(() => {
      if (touchpoint) return parseArrayResponse(touchpoint.questions);
      return [];
    }, [touchpoint]);

    useEffect(() => {
      if (touchpoint) {
        const {
          state,
          touchpointable,
          university_required_state,
          study_year_required_state,
          degree_required_state,
          study_subject_required_state,
          cv_required_state,
          linkedin_profile_required_state,
          show_guest_list,
        } = touchpoint;

        const {
          id: touchpointableIdResponse,
          capacity,
          enforce_capacity = false,
          waitlist,
          enforce_waitlist_for_all,
        } = parseResponse(touchpointable);
        setUniversityRequiredState(university_required_state);
        setStudyYearRequiredState(study_year_required_state);
        setDegreeRequiredState(degree_required_state);
        setSubjectRequiredState(study_subject_required_state);
        setCVRequiredState(cv_required_state);
        setLinkedInProfileRequiredState(linkedin_profile_required_state);
        setCustomQuestionsList(questions);
        setTouchpointableId(touchpointableIdResponse);
        setEnforceCapacity(enforce_capacity);
        setCapacity(capacity);
        setEnableWaitlist(waitlist);
        setShowGuestList(show_guest_list);
        setEnableForceWaitlist(enforce_waitlist_for_all);
        setState(state);
        setAvailableTouchpointData({
          touchpointable_attributes: {
            id: touchpointableIdResponse,
            capacity,
            enforce_capacity,
            waitlist,
            enforce_waitlist_for_all,
          },
          state,
          university_required_state,
          study_year_required_state,
          degree_required_state,
          study_subject_required_state,
          cv_required_state,
          linkedin_profile_required_state,
          questions_attributes: questions,
          removedCustomQuestionIds: [],
        });
      }
    }, [touchpoint]);

    const nextState = useMemo(() => {
      if (!state) return TouchpointState.DRAFT;

      if (state === TouchpointState.DRAFT)
        return isSocietyEvent
          ? TouchpointState.PUBLISHED
          : TouchpointState.READY_FOR_APPROVAL;
      if (state === TouchpointState.APPROVED) return TouchpointState.PUBLISHED;
      else return state;
    }, [state]);

    const areValuesEdited = (submitType?: TouchpointState) => {
      if (touchpoint) {
        const selectedAttributes = {
          touchpointable_attributes: {
            id: touchpointableId,
            capacity: enforceCapacity
              ? capacity
                ? Number(capacity)
                : null
              : null,
            enforce_capacity: enforceCapacity,
            waitlist: enableWaitlist,
            enforce_waitlist_for_all: enableForceWaitlist,
          },
          state:
            submitType === TouchpointState.SAVE ||
            submitType === TouchpointState.DRAFT
              ? state
              : nextState,
          university_required_state: isUniversityRequiredState,
          study_year_required_state: isStudyYearRequiredState,
          degree_required_state: isDegreeRequiredState,
          study_subject_required_state: isSubjectRequiredState,
          cv_required_state: isCVRequiredState,
          linkedin_profile_required_state: isLinkedInProfileRequiredState,
          questions_attributes: customQuestionsList,
          removedCustomQuestionIds,
        };

        return !isEqual(selectedAttributes, availableTouchpointData);
      } else {
        return true;
      }
    };

    const onClose = async () => {
      if (areValuesEdited()) {
        const flag = await confirmationModalInstance();
        if (flag === ConfirmationButtonResponse.SAVE) {
          onSubmit({ checkRequired: false, submitType: TouchpointState.SAVE });
        } else if (flag === ConfirmationButtonResponse.DONT_SAVE) {
          if (onboarding) {
            router.push(
              isManager
                ? PAGE_ROUTES.DASHBOARD
                : PAGE_ROUTES.RECRUITER_DASHBOARD
            );
            return;
          }
          router.push(PAGE_ROUTES.CAMPAIGNS);
        }
      } else {
        if (onboarding) {
          router.push(
            isManager ? PAGE_ROUTES.DASHBOARD : PAGE_ROUTES.RECRUITER_DASHBOARD
          );
          return;
        }
        mutateEvents();
        router.push(PAGE_ROUTES.CAMPAIGNS);
      }
    };

    const onNavigate = (goToStep: number) => {
      if (goToStep === step) return;
      setIsSubmitted(true);
      if (isInvalid) return;
      setIsSubmitted(false);

      onSubmit({
        checkRequired: false,
        submitType:
          state === TouchpointState.DRAFT ? TouchpointState.DRAFT : undefined,
        goToStep,
      });
    };

    const onChangeCustomQuesState = (
      questionId: string,
      state: QuestionState
    ) => {
      setSelectedQuestionId('');
      if (state === QuestionState.DELETE) {
        const deletedQues = customQuestionsList.find(
          (q) => q.id === questionId
        );
        if (deletedQues && !deletedQues.isNew) {
          setRemovedCustomQuestionIds((questionIds) => [
            ...questionIds,
            questionId,
          ]);
        }
        setCustomQuestionsList((list) =>
          list.filter((ques) => ques.id !== questionId)
        );
      } else if (state === QuestionState.EDIT) {
        setSelectedQuestionId(questionId);
      } else {
        setCustomQuestionsList((list) =>
          list.map((ques) => {
            if (ques.id === questionId) {
              return {
                ...ques,
                state,
              };
            } else return ques;
          })
        );
      }
    };

    const handleAddQuestion = (
      event: React.MouseEvent<HTMLButtonElement>,
      {
        title,
        isRequired,
        optionsList,
        questionType,
        isEdit,
      }: {
        optionsList: Array<string>;
        isRequired: boolean;
        questionType: string;
        title: string;
        isEdit: boolean;
      }
    ) => {
      event.stopPropagation();
      event.preventDefault();
      const data = {
        ...selectedQuestion,
        title,
        state: isRequired ? QuestionState?.REQUIRED : QuestionState.OPTIONAL,
        question_type: questionType,
        ...((questionType && questionType === 'single_choice') ||
        (questionType === 'multiple_choice' && optionsList?.length)
          ? { options: optionsList }
          : {}),
      };
      if (isEdit) {
        setCustomQuestionsList((prevQues) =>
          prevQues.map((q) => {
            if (q.id === selectedQuestion?.id) {
              return { ...data };
            } else {
              return q;
            }
          })
        );
      } else {
        setCustomQuestionsList([
          ...customQuestionsList,
          {
            id: uuidv4(),
            title,
            state: isRequired
              ? QuestionState?.REQUIRED
              : QuestionState.OPTIONAL,
            isNew: true,
            question_type: questionType,
            ...((questionType && questionType === 'single_choice') ||
            (questionType === 'multiple_choice' && optionsList?.length)
              ? { options: optionsList }
              : {}),
          },
        ]);
      }
      setIsOpenCustomQuestionModal(false);
      setSelectedQuestion(null);
    };

    const onSubmit = ({
      checkRequired,
      submitType,
      goToStep,
      isPreviewClicked,
    }: {
      checkRequired: boolean;
      submitType?: TouchpointState;
      goToStep?: number;
      isPreviewClicked?: boolean;
    }) => {
      const onSaveRequest = () => {
        const isValueEdited = areValuesEdited(submitType);

        const data = {
          touchpointable_attributes: {
            id: touchpointableId || '',
            capacity: enforceCapacity
              ? capacity
                ? Number(capacity)
                : null
              : null,
            enforce_capacity: enforceCapacity,
            waitlist: enableWaitlist,
            enforce_waitlist_for_all: enableForceWaitlist,
            show_guest_list: showGuestList,
          },
          state:
            submitType === TouchpointState.SAVE ||
            submitType === TouchpointState.DRAFT
              ? state || TouchpointState.DRAFT
              : nextState,
          ...(!isSocietyEvent
            ? {
                submitType:
                  state === TouchpointState.READY_FOR_APPROVAL ||
                  state === TouchpointState.PUBLISHED
                    ? TouchpointState.SAVE
                    : submitType,
              }
            : { submitType }),
          ...(isSocietyEvent
            ? {
                enabled:
                  submitType === TouchpointState.SAVE ||
                  submitType === TouchpointState.DRAFT
                    ? false
                    : state === TouchpointState.PUBLISHED ||
                      nextState === TouchpointState.PUBLISHED,
              }
            : {}),
          university_required_state: isUniversityRequiredState,
          study_year_required_state: isStudyYearRequiredState,
          degree_required_state: isDegreeRequiredState,
          study_subject_required_state: isSubjectRequiredState,
          cv_required_state: isCVRequiredState,
          linkedin_profile_required_state: isLinkedInProfileRequiredState,
          questions_attributes: customQuestionsList,
          removedCustomQuestionIds,
          ...(isSocietyEvent
            ? { notify_subscribers: isNotifySubscribers }
            : {}),
        };

        onSave({ data, isValueEdited, goToStep, isPreviewClicked });
      };
      if (checkRequired) {
        setIsSubmitted(true);
        if (isInvalid) return;
      }

      onSaveRequest();
    };

    const onPreviewClick = () => {
      if (touchpoint) {
        onSubmit({
          checkRequired: false,
          submitType: TouchpointState.SAVE,
          isPreviewClicked: true,
        });
        const { slug } = touchpoint;
        const publicPageUrl = `${
          window.location.origin
        }${getOpportunityPublicPagePathname(TouchpointType.Event, slug || '')}`;
        window.open(publicPageUrl, '_blank');
      }
    };
    const primaryButtonText = useMemo(() => {
      if (
        state === TouchpointState.READY_FOR_APPROVAL ||
        state === TouchpointState.PUBLISHED
      )
        return t('event-posting.save-changes');
      if (state === TouchpointState.APPROVED || isSocietyEvent)
        return t('event-posting.publish');
      if (state === TouchpointState.DRAFT) return 'Submit for approval';
      else return t('event-posting.submit-for-approval');
    }, [state]);

    const isInvalid =
      (enforceCapacity && !capacity) || capacity
        ? !REGEX.POSITIVE_NUMBER.test(capacity?.toString() || '')
        : false;
    const defaultQuestionOptions = useMemo(() => {
      return QuestionOptions.filter((ques) => !ques?.isDelete && !ques.isEdit);
    }, [QuestionOptions]);

    return (
      <>
        <Header
          step={step as number}
          onClose={onClose}
          onNavigate={(goToStep: number) => onNavigate(goToStep)}
          onNext={() => onSubmit({ checkRequired: true })}
          onNextLoading={isLoading}
          onPreviewClick={onPreviewClick}
          primaryButtonText={primaryButtonText}
          className={styles.header}
        />
        <div className={contentClassNames} style={style} data-testid="Step1">
          <div className={styles.container}>
            <div
              className={cn({
                [styles.containerSociety]: isSocietyEvent,
              })}
            >
              <section className={styles.section}>
                <main className={styles.main}>
                  <p className={styles.title}>
                    {t('event-posting.registration-setting')}
                  </p>
                </main>
                <div className={styles.separator}></div>
                <main className={styles.main}>
                  <Row
                    align="center"
                    justify="space-between"
                    className={styles.registrationSettingRow}
                  >
                    <p className={cn(styles.StepLabel, styles.waitlistLabel)}>
                      {t('event-posting.require-approval')}
                    </p>
                    <Toggle
                      className={styles.toggle}
                      checked={enableForceWaitlist}
                      onChange={() => {
                        setEnableForceWaitlist((flag) => {
                          if (!flag) {
                            setEnforceCapacity(false);
                          }
                          return !flag;
                        });
                      }}
                    />
                  </Row>

                  <div className={styles.registrationSettingRow}>
                    <Row align="center" justify="space-between">
                      <p className={cn(styles.StepLabel, styles.waitlistLabel)}>
                        {t('event-posting.set-event-capacity')}
                      </p>
                      <Toggle
                        className={styles.toggle}
                        checked={enforceCapacity}
                        onChange={() => {
                          setEnforceCapacity((flag) => {
                            if (!flag) {
                              setEnableForceWaitlist(false);
                            }
                            return !flag;
                          });
                        }}
                      />
                    </Row>
                    {enforceCapacity ? (
                      <>
                        <div className={styles.gap}></div>
                        <Input
                          type="number"
                          label="Maximum Capacity"
                          placeholder="Enter maximum capacity"
                          value={capacity}
                          onChange={(event) =>
                            setCapacity(parseInt(event.target.value))
                          }
                          isValid={
                            isSubmitted
                              ? !!capacity &&
                                REGEX.POSITIVE_NUMBER.test(capacity.toString())
                              : true
                          }
                          onWheel={(e) => {
                            const target = e.target as HTMLElement;
                            target.blur();
                          }}
                          className={cn(styles.input)}
                        />
                      </>
                    ) : null}
                  </div>

                  <Row
                    align="center"
                    justify="space-between"
                    className={styles.registrationSettingRow}
                  >
                    <p className={cn(styles.StepLabel, styles.waitlistLabel)}>
                      {t('event-posting.enable-waitlist')}
                    </p>
                    <Tooltip
                      content={
                        capacity && enforceCapacity
                          ? t('event-posting.enable-waitlist-tooltip-1')
                          : t('event-posting.enable-waitlist-tooltip-2')
                      }
                      position="top"
                      xAlign="center"
                      color="dark"
                      className={cn(styles.tooltip, styles.waitlist, {
                        [styles.big]: capacity && enforceCapacity,
                      })}
                    >
                      <Toggle
                        className={styles.toggle}
                        checked={enableWaitlist}
                        onChange={() => setEnableWaitlist((flag) => !flag)}
                        disabled={!enforceCapacity || !capacity}
                      />
                    </Tooltip>
                  </Row>
                  <>
                    <div className={styles.gap}></div>
                    <Row
                      align="center"
                      justify="space-between"
                      className={styles.waitlistRow}
                    >
                      <p className={cn(styles.StepLabel, styles.waitlistLabel)}>
                        {t('event-posting.show-guest-list')}
                      </p>
                      <Tooltip
                        content={t('event-posting.guest-list-tooltip')}
                        position="top"
                        xAlign="center"
                        color="dark"
                        className={styles.tooltip}
                      >
                        <Toggle
                          className={styles.toggle}
                          checked={showGuestList}
                          onChange={() => setShowGuestList((flag) => !flag)}
                        />
                      </Tooltip>
                    </Row>
                  </>
                </main>
              </section>
              <section className={styles.section}>
                <main className={styles.main}>
                  <p className={styles.title}>
                    {t('event-posting.registration-questions')}
                    <span>({t('event-posting.optional')})</span>
                  </p>
                  <p className={styles.desc}>
                    <Trans
                      t={t}
                      i18nKey="events-posting.details-collection-info"
                    >
                      We collect <span>Name</span> and <span>Email</span> by
                      default. Ask only for information you must know about your
                      attendees, asking too many questions may scare them away.
                      😱
                    </Trans>
                  </p>
                </main>
                <div className={styles.separator}></div>
                <main className={styles.main}>
                  <TouchpointQuestions
                    isSubjectRequiredState={isSubjectRequiredState}
                    setSubjectRequiredState={setSubjectRequiredState}
                    isCVRequiredState={isCVRequiredState}
                    setCVRequiredState={setCVRequiredState}
                    isLinkedInProfileRequiredState={
                      isLinkedInProfileRequiredState
                    }
                    setLinkedInProfileRequiredState={
                      setLinkedInProfileRequiredState
                    }
                    isUniversityRequiredState={isUniversityRequiredState}
                    setUniversityRequiredState={setUniversityRequiredState}
                    customQuestionsList={customQuestionsList}
                    isStudyYearRequiredState={isStudyYearRequiredState}
                    setStudyYearRequiredState={setStudyYearRequiredState}
                    isDegreeRequiredState={isDegreeRequiredState}
                    setDegreeRequiredState={setDegreeRequiredState}
                    QuestionOptions={QuestionOptions}
                    onChangeCustomQuesState={(...params) =>
                      onChangeCustomQuesState(...params)
                    }
                    defaultQuestionOptions={defaultQuestionOptions}
                    dropDownClassName={styles.dropdownQues}
                    questionListClassName={styles.questionList}
                    customQuesClassName={styles.customQuesRow}
                  />
                  <ButtonV3
                    size="small"
                    startIcon="plus"
                    variant="contained"
                    color="secondary"
                    type="button"
                    onClick={() => {
                      setSelectedQuestion(null);
                      setSelectedQuestionId('');
                      setIsOpenCustomQuestionModal(true);
                    }}
                    className={styles.questionBtn}
                  >
                    {t('event-posting.add-custom-questions')}
                  </ButtonV3>
                </main>
              </section>
            </div>

            <Row className={styles.buttonRow}>
              {state === TouchpointState.DRAFT ? (
                <ButtonV3
                  color="secondary"
                  onClick={() =>
                    onSubmit({
                      checkRequired: false,
                      submitType: TouchpointState.DRAFT,
                    })
                  }
                  disabled={isSubmitted}
                  isLoading={isDraftLoading}
                  className={styles.nextSecondary}
                >
                  {t('event-posting.save-as-draft')}
                </ButtonV3>
              ) : null}
              {primaryButtonText ? (
                <ButtonV3
                  type="submit"
                  onClick={() => onSubmit({ checkRequired: true })}
                  isLoading={isLoading}
                  className={styles.submit}
                  disabled={isSubmitted && isInvalid}
                >
                  {primaryButtonText}
                </ButtonV3>
              ) : null}
            </Row>
            {isSocietyEvent && touchpoint?.notify_subscribers === null ? (
              <Checkbox
                checked={isNotifySubscribers}
                labelTitle={t('event-posting.notify-subscriber-about-event')}
                onChange={(e) => setIsNotifySubscribers(e?.target?.checked)}
                className={styles.checkbox}
              >
                <p className={styles.checkboxLabel}>
                  {t('event-posting.notify-subscriber-about-event')}
                </p>
              </Checkbox>
            ) : null}
          </div>
        </div>
        {isOpenCustomQuestionModal ? (
          <CustomQuestionModal
            isOpen={isOpenCustomQuestionModal}
            onClose={() => setIsOpenCustomQuestionModal(false)}
            onAddQuestion={handleAddQuestion}
            questionTypes={questionTypes}
            selectedQuestion={selectedQuestion}
          />
        ) : null}
      </>
    );
  }
);

Step3.displayName = 'Step3';
